import {Injectable} from "@angular/core";
import {DatePipe} from "@angular/common";
import {ColDef} from "ag-grid-community";
import {AuditEventTypesPipe} from "../pipes/audit-event-types.pipe";
import {CopyValueComponent} from "../components/cell-renderers/copy-value/copy-value.component";
import {EventSourceToDisplayPipe} from "../pipes/event-source-to-display.pipe";
import {ErrorTypePipe} from "../pipes/error-type.pipe";

@Injectable({
  providedIn: 'root'
})
export class AuditEventTables {

  private idColDef: ColDef = {
    headerName: 'Audit Id',
    field: 'Id',
    resizable: true,
    sortable: true
  };

  private createdOnColDef: ColDef = {
    headerName: 'Created On',
    field: 'CreatedOn',
    resizable: true,
    valueFormatter: params => {
      const convertedDate = this.datePipe.transform(params.value, 'MMM d, y HH:mm');
      return convertedDate ? convertedDate : '';
    },
    sortable: true
  };

  private eventTypeColDef: ColDef = {
    headerName: 'Audit Event Type',
    field: 'AuditEventTypeId',
    resizable: true,
    valueFormatter: params => {
      return this.auditEventTypesPipe.transform(params.value);
    },
    sortable: true
  };

  private playerIdColDef: ColDef = {
    headerName: 'Player Id',
    field: 'PlayerId',
    cellRenderer: CopyValueComponent,
    resizable: true,
    sortable: true
  };

  private adminIdColDef: ColDef = {
    headerName: 'Admin Id',
    field: 'AdminId',
    cellRenderer: CopyValueComponent,
    resizable: true,
    sortable: true
  };

  private playerEmailColDef: ColDef = {
    headerName: 'Player Email',
    field: 'PlayerEmail',
    cellRenderer: CopyValueComponent,
    resizable: true,
    sortable: true
  };

  private adminEmailColDef: ColDef = {
    headerName: 'Admin Email',
    field: 'AdminEmail',
    cellRenderer: CopyValueComponent,
    resizable: true,
    sortable: true
  };

  private eventSourceIdColDef: ColDef = {
    headerName: 'Event Source',
    field: 'AuditEventSourceTypeId',
    resizable: true,
    sortable: true,
    valueFormatter: params => {
      return this.eventSourceToDisplayPipe.transform(params.value);
    },
  };

  private gameColDef: ColDef = {
    headerName: 'Game',
    field: 'GameName',
    resizable: true,
    // Not sortable: the grid is server-paged, so it would sort the loaded page and look like it had
    // gathered the game's rows together.
    sortable: false,
    valueFormatter: params => params.value ?? ''
  };

  // A player audit with no game can't say which game it was for, so it is shown under every game.
  private playerGameColDef: ColDef = {
    ...this.gameColDef,
    valueFormatter: params => params.value ?? 'All games'
  };

  private isSuccessColDef: ColDef = {
    headerName: "Event Success",
    field: "IsSuccess",
    cellDataType: 'text',
    resizable: true,
    sortable: true,
    valueFormatter: params => {
      if (params.value == null)
      {
        return "N/A";
      }
      return params.value ? "Success" : "Failure";
    },
    cellStyle: params => {
      if (params.value == null) {
        return null;
      }
      return params.value ? {color: 'green'} : {color: 'red'};
    }
  }

  public playerAuditEventColDefs: ColDef[] = [
    this.idColDef,
    this.createdOnColDef,
    this.playerEmailColDef,
    this.playerIdColDef,
    this.playerGameColDef,
    this.eventTypeColDef,
    this.eventSourceIdColDef,
    this.isSuccessColDef
  ];

  public adminAuditEventColDefs: ColDef[] = [
    this.idColDef,
    this.createdOnColDef,
    this.adminEmailColDef,
    this.adminIdColDef,
    this.gameColDef,
    this.eventTypeColDef,
    this.eventSourceIdColDef,
    this.isSuccessColDef
  ];

  public systemAuditEventColDefs: ColDef[] = [
    this.idColDef,
    this.createdOnColDef,
    this.gameColDef,
    this.eventTypeColDef,
    this.isSuccessColDef
  ];

  private sourceTypeColDef: ColDef = {
    headerName: 'Source',
    field: 'SourceType',
    resizable: true,
    sortable: true,
    cellStyle: params => {
      switch (params.value) {
        case 'Admin':
          return {color: '#007bff'};
        case 'Player':
          return {color: '#28a745'};
        case 'System':
          return {color: '#6c757d'};
        default:
          return null;
      }
    }
  };

  public failedAuditEventColDefs: ColDef[] = [
    this.createdOnColDef,
    this.sourceTypeColDef,
    this.gameColDef,
    this.eventTypeColDef,
    this.adminEmailColDef,
    this.playerEmailColDef,
    this.adminIdColDef,
    this.playerIdColDef
  ];

  constructor(private datePipe: DatePipe,
              private eventSourceToDisplayPipe: EventSourceToDisplayPipe,
              private auditEventTypesPipe: AuditEventTypesPipe,
              private errorTypePipe: ErrorTypePipe) {
  }

}
